import { useIsOnDraftPage } from "@/hooks/use-is-on-draft-page";
import { usePollNewMergedRevision } from "@/hooks/use-poll-new-merged-revision";
import { changeMode } from "@/store/mode-slice";
import { selectHasChangesInDraft } from "@/store/revisions-selector";
import { useAppSelector, useAppStore } from "@/store/store-hooks";
import {
  useNavigateToDraftView,
  useNavigateToLiveView,
} from "@/utils/redirects";
import { FaroToggleGroup, FaroTooltip, ToggleItem } from "@faro-lotv/flat-ui";
import {
  selectIsProjectSnapshot,
  selectProjectId,
} from "@faro-lotv/project-source";
import { useCallback, useState, type JSX } from "react";

type LabelType = "live" | "draft";

type LiveDraftToggleButtonProps = {
  /** Optional callback to run before navigation (e.g., to save edits). */
  onBeforeNavigate?(): Promise<void>;
};

/**
 * @returns The Live/Draft toggle button displayed as the last item of the breadcrumb menu which
 * redirects to Data Management.
 * It's only visible if the user has write permissions and there are scans in the project or
 * there's an open draft revision.
 * This visibility check is handled by the parent component (ProjectBreadcrumbs).
 * If there are changes in the open draft revision, a dot indicator is shown in the "Draft" button.
 * If there's a newer merged revision than the one currently viewed, a dot indicator is shown in
 * the "Live" button.
 */
export function LiveDraftToggleButton({
  onBeforeNavigate,
}: LiveDraftToggleButtonProps = {}): JSX.Element {
  const { dispatch } = useAppStore();

  const projectId = useAppSelector(selectProjectId);
  const isProjectSnapshot = useAppSelector(selectIsProjectSnapshot);
  const navigateToLiveView = useNavigateToLiveView();
  const navigateToDraftView = useNavigateToDraftView();
  const isOnDraftPage = useIsOnDraftPage();
  const hasDraftChanges = useAppSelector(selectHasChangesInDraft);
  const [hasNewMergedRevision, setHasNewMergedRevision] = useState(false);
  const callbackHasNewMergedRevision = useCallback((): void => {
    setHasNewMergedRevision(true);
  }, []);
  usePollNewMergedRevision(callbackHasNewMergedRevision);

  const items: Array<ToggleItem<LabelType>> = [
    {
      value: "live",
      label: "Live",
      hasIndicator: hasNewMergedRevision,
    },
    {
      value: "draft",
      label: "Draft",
      hasIndicator: hasDraftChanges,
      isDisabled: isProjectSnapshot,
    },
  ];

  const tooltip = isOnDraftPage
    ? "Enter Live view to explore and interact with your project. Review, gain insights, and share data."
    : "Enter Draft view to manage, process and register your scans.";

  const handleToggleChange = useCallback(async () => {
    if (onBeforeNavigate) {
      await onBeforeNavigate();
    }

    if (!projectId) {
      return;
    }

    if (isOnDraftPage) {
      // Go back to start mode, to correctly initialize the camera from previous view
      dispatch(changeMode("start"));
      navigateToLiveView(projectId);
    } else {
      navigateToDraftView(projectId, "toggleButton");
    }
  }, [
    onBeforeNavigate,
    projectId,
    isOnDraftPage,
    dispatch,
    navigateToLiveView,
    navigateToDraftView,
  ]);

  return projectId ? (
    <FaroTooltip title={tooltip}>
      <FaroToggleGroup
        items={items}
        value={isOnDraftPage ? "draft" : "live"}
        onChange={handleToggleChange}
        sxButtons={{ py: 0 }}
        sxLabels={{ fontSize: "12px" }}
      />
    </FaroTooltip>
  ) : (
    <span>Live</span>
  );
}
